import FileAttachment from "./shared/FileAttachment";
import { useImpactedModules } from "../../hooks/useImpactedModules";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "contact", label: "Contact info", desc: "Personal details" },
  { id: "incident", label: "Incident details", desc: "Product and IDs" },
  { id: "attachments", label: "Attachments", desc: "Relevant files & logs" },
];

const INITIAL_STATE = {
  contactName: "",
  email: "",
  phone: "",
  country: "",
  summary: "",
  product: "AFS",
  platform: "",
  loanId: "",
  carId: "",
  dealerId: "",
  description: "",
  attachments: [],
};

const COUNTRIES = [
  "Nigeria",
  "Ghana",
  "Kenya",
  "Uganda",
  "Cote d'Ivoire",
  "Senegal",
  "South Africa",
  "Morocco",
  "Egypt",
];
const PRODUCTS = ["AFS", "Loans", "Asset Management"];



export default function AFSLoansForm({ onBack }) {
  const { modules: impactedModules, isLoading: modulesLoading } = useImpactedModules();
  const profile = safeLocalStorageGet("staff_profile");
  const defaultCountry = getDefaultCountry(profile);
  const { tText } = useTranslate();

  const {
    formData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState({
    ...INITIAL_STATE,
    country: defaultCountry,
    contactName: profile.firstname
      ? `${profile.firstname} ${profile.lastname}`
      : "",
    email: profile.primaryEmail || profile.email || "",
  });

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy, creatorName: profileCreatorName } = getSubmitterInfo(profile);
    const submittedByEmail = formData.email || submittedBy;
    const creatorName = formData.contactName || profileCreatorName;

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: formData.contactName,
        category: 1,
        Summary: formData.summary,
        description: formData.description,
        impactedsystem: formData.platform ? [formData.platform] : [],
        priority: "Medium",
        environment: "staging",
        loanid: formData.loanId || "",
        carid: formData.carId || "",
        dealerid: formData.dealerId || "",
        Description: `Product: ${formData.product}\n\n${formData.description}\n\nPhone: ${formData.phone || "N/A"}`,
        Country: formData.country,
        sourceofrequest: "Staff Portal",
        confluencelink: "",
        accesstype: "",
        accesslevel: "",
        linemanagername: "",
        linemanageremail: "",
        mailinglist: "",
        status: "Open",
        tickettype: "AFS & Loans",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedByEmail,
        attachement: attachments.map(a => a.id)
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="Internal Support"
        title="AFS & Loans"
        subtitle="Submit an AFS incident or raise a loan-related concern."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        currentBreadcrumb="AFS & Loans"
      >
        <SuccessCard
          title="Incident Submitted"
          description="Your AFS/Loan incident has been recorded. Our support team will review it and update you shortly."
          onBack={onBack}
        />
      </SplitFormLayout>
    );
  }

  const formValid =
    formData.contactName &&
    formData.email &&
    formData.country &&
    formData.summary &&
    formData.product &&
    formData.platform &&
    formData.loanId &&
    formData.description;

  return (
    <SplitFormLayout
      badge="Internal Support"
      title="AFS & Loans"
      subtitle="Submit an AFS incident or raise a loan-related concern."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      currentBreadcrumb="AFS & Loans"
    >
      <FormProvider value={contextValue}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className={styles.ticketFormWrapper}
      >
        {error && <div className={styles.errorBanner}>{tText(error)}</div>}

        <FormSection id="contact" title="1. Contact Information">
          <div className={styles.row2}>
            <FormField label="Contact Name" name="contactName" required>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("e.g. Tunde Usman")}
                required
              />
            </FormField>
            <FormField label="Email Address" name="email" required>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g. tunde@autochek.africa"
                required
              />
            </FormField>
          </div>
          <div className={styles.row2} style={{ marginTop: "20px" }}>
            <FormField label="Phone Number" name="phone">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g. +234..."
              />
            </FormField>
            <FormField label="Country" name="country" required>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{tText("Select...")}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{tText(c)}</option>
                ))}
              </select>
            </FormField>
          </div>
        </FormSection>

        <FormSection id="incident" title="2. Incident Details">
          <FormField label="Summary" name="summary" required style={{ marginBottom: "20px" }}>
            <input
              type="text"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("Brief summary")}
              required
            />
          </FormField>
          <div className={styles.row2} style={{ marginBottom: "20px" }}>
            <FormField label="Product" name="product" required>
              <select
                name="product"
                value={formData.product}
                onChange={handleChange}
                className={styles.select}
                required
              >
                {PRODUCTS.map((p) => (
                  <option key={p} value={p}>{tText(p)}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Platform" name="platform" required>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{modulesLoading ? tText("Loading...") : tText("Select...")}</option>
                {impactedModules.map((p) => (
                  <option key={p} value={p}>{tText(p)}</option>
                ))}
              </select>
            </FormField>
          </div>
          <div className={styles.row3} style={{ marginBottom: "20px" }}>
            <FormField label="Loan ID" name="loanId" required>
              <input
                type="text"
                name="loanId"
                value={formData.loanId}
                onChange={handleChange}
                className={styles.input}
                placeholder="LN-..."
                required
              />
            </FormField>
            <FormField label="Car ID" name="carId">
              <input
                type="text"
                name="carId"
                value={formData.carId}
                onChange={handleChange}
                className={styles.input}
                placeholder="CAR-..."
              />
            </FormField>
            <FormField label="Dealer ID" name="dealerId">
              <input
                type="text"
                name="dealerId"
                value={formData.dealerId}
                onChange={handleChange}
                className={styles.input}
                placeholder="DL-..."
              />
            </FormField>
          </div>
          <FormField label="Description" name="description" required>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              rows={4}
              placeholder={tText("Details...")}
              required
            />
          </FormField>
        </FormSection>

        <FormSection id="attachments" title="3. Attachments">
          <FileAttachment
            attachments={formData.attachments}
            onChange={handleAddFiles}
            onRemove={handleRemoveFile}
          />
        </FormSection>

        <PinnedSubmit
          isSubmitting={isSubmitting}
          isValid={formValid}
          onSubmit={onSubmit}
          onInvalidSubmit={setAllTouched}
          label="Submit incident"
        />
      </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
